import React, { CSSProperties, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Button, CircularProgress, Icon, makeStyles, Theme, useTheme } from '@material-ui/core';
import clsx from 'clsx';
import { capitalizeAll, ModeTheme, resolutionToMilliseconds, Time, useScreenSizeCheck, XYChartType } from '@moderepo/mode-app-components';
import moment from 'moment';
import { SmartModuleType } from '@moderepo/mode-internal-apis';
import { modeUIContext, removeControlPanelComp, addControlPanelComp, showSlideOutPanel, SlideOutPanelLocation, ControlPanelCompPriority }
    from '@moderepo/mode-ui-state';
import produce, { Draft } from 'immer';
import { isMetricsAlert } from '@moderepo/mode-apis';
import { useSelectAlert, useSelectEntity, useSelectProject } from '../../utils/dataStateHooks';
import { useRitzTranslation } from '../../utils/customHooks';
import { createUniqueMetricId, FetchAggregatedDataOptions, FetchCollectionDataParams, FetchDataNumericMetricParams, FetchDataTagMetricParams,
    getMetricsDefinitionMetricsId, MultiDataSourcesViewContainer, MultiDataSourcesViewContainerProps, useSelectTranslatedEntityClasses } from '..';
import { RitzComponentsCollection } from '../../utils/ritzComponentsCollection';
import { AlertPreviewCompOptions, SlideOutPanelCompType, EntityPreviewCompOptions } from '../routes/defaultProjectConfig';
import { TimeControlType, TimeRangeControlBarContainer, TimeRangeControlSettings } from '../misc';



const useStyle = makeStyles((theme: Theme) => {
    return {
        root: {
        },


        controlBar: {
            backgroundColor: theme.palette.background.default,
            padding        : theme.spacing(1),
            display        : 'flex',
            flexDirection  : 'row',
            alignItems     : 'center',
            justifyContent : 'space-between',
        },

        timeControl: {
            flex : 1,
            width: '100%',
        },

        actionsContainer: {
            display       : 'flex',
            flexDirection : 'row',
            alignItems    : 'center',
            justifyContent: 'flex-end',
            marginTop     : theme.spacing(1),
            flex          : 1,
            width         : '100%',
        },

        action: {
            whiteSpace          : 'nowrap',
            '&:not(:last-child)': {
                marginRight: theme.spacing(1),
            },
        },

        [theme.breakpoints.up('md')]: {
            controlBar: {
                padding: theme.spacing(2),
            },

            actionsContainer: {
                marginTop: theme.spacing(0),
                flex     : 0,
                width    : 'auto',
            },

            action: {
                '&:not(:last-child)': {
                    marginRight: theme.spacing(2),
                },
            },
        },

        [theme.breakpoints.up('lg')]: {
            controlBar: {
                padding: theme.spacing(2, 3),
            },
        },
    };
}, { name: 'InvestigateAlertContainer', index: 1 });



// The +/- number of interval of data we want to show to the user initially at the time the alert occurred. For example, if the alert rule
// is configured to monitor the data once every 10 minutes. This mean the 1 interval = 10 minutes. We will show data 5x10 minutes before and
// 5x10 minutes after that the time the alert occurred.
const DEFAULT_DATA_INTERVALS = 5;


const INVESTIGATE_ALERT_CONTROL_BAR = 'Investigate_Alert_Control_Bar';



/**
 * Create metric settings for the MultiDataSourcesViewContainer
 */
const metricViewSettings: MultiDataSourcesViewContainerProps['metricViewSettings'] = {
    numericMetrics: {
        maxInitiallySelected  : 1,
        hideMetricsWithoutData: true,
        maxLegends            : 5,
        showLegends           : true,
        allowUnselect         : true,
    },
    tagMetrics: {
        maxInitiallySelected  : 0,
        hideMetricsWithoutData: true,
        maxLegends            : 5,
        showLegends           : true,
        allowUnselect         : true,
    },
};


/**
 * The settings for the time input control
 */
const timeControlSettings: TimeRangeControlSettings = {
    type: TimeControlType.TIME_RANGE,
};


/**
 * Create chart settings for the MultiDataSourcesViewContainer
 * The settings for the vertical line style
 */
const chartViewSettings: MultiDataSourcesViewContainerProps['chartViewSettings'] = {
    numericChartSettings: {
        shareYAxis    : false,
        chartType     : XYChartType.LINE,
        connectDataGap: false,
    },
    verticalLineStyle: {
        color  : '#ff0000',
        opacity: 50,
        width  : 3,
    },
};



export interface InvestigateAlertContainerProps {
    readonly projectId: number;
    readonly alertId: string;
    readonly className?: string | undefined;
    readonly style?: CSSProperties | undefined;
}


/**
 * This is a container that display data for the entity that is tied to an Alert so that the user can look at the data to investigate the
 * problem with the entity.
 */
export const InvestigateAlertContainer: React.FC<InvestigateAlertContainerProps> = (props: InvestigateAlertContainerProps) => {

    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const project = useSelectProject(props.projectId);
    const alert = useSelectAlert(props.projectId, props.alertId);
    const selectedEntity = useSelectEntity(props.projectId, alert?.entityId);
    const theme = useTheme() as ModeTheme;
    const { trans } = useRitzTranslation(project);
    const entityClasses = useSelectTranslatedEntityClasses(props.projectId, trans);
    const classes = useStyle();
    const isTabletOrLarger = useScreenSizeCheck('md');

    /**
     * State to keep track of the fetch data time range options
     */
    const [fetchDataOptions, setFetchDataOptions] = useState<FetchAggregatedDataOptions>();



    /**
     * This useEffect is used for initializing the fetchDataOptions once when the `alert` object is loaded. We need to wait
     * until the alert is loaded so that we can initialize the "fetchDataOptions" at the time the alert occurs.
     */
    useEffect(() => {
        if (alert) {
            // We will show data at the time the alert occurs
            const invocationTime = alert.invocationTime;

            // We will show +/- (DEFAULT_DATA_INTERVALS x N) milliseconds from the invocationTime. N will depends on the alert condition's duration.
            // If the alert condition monitor the data every 10 minutes then N will be 10 minutes
            // NOTE: alert.condition.duration is in string format e.g. "1min", "10min", "1hour", etc... Therefore we will need to call
            // resolutionToMilliseconds function to convert it to number of milliseconds. Default is 1 hour if we are unable to convert it.
            const intervalLength = resolutionToMilliseconds(alert.condition.duration) ?? Time.HOUR_IN_MS;

            setFetchDataOptions({
                startTime: moment(invocationTime).subtract(DEFAULT_DATA_INTERVALS * intervalLength, 'milliseconds').valueOf(),
                endTime  : moment(invocationTime).add(DEFAULT_DATA_INTERVALS * intervalLength, 'milliseconds').valueOf(),
            });
        }
    }, [alert]);



    /**
     * Handler for start/end time changed from the control bar. When the time range changed, we will update the fetchDataOptions which will
     * be passed to the MultiDataSourcesViewContainer and the MultiDataSourcesViewContainer will refetch data based on the selected start/end time.
     */
    const onTimeRangeChanged = useCallback((startTime: number, endTime: number) => {
        setFetchDataOptions((currentTime) => {
            return produce(currentTime, (draft: Draft<typeof currentTime>) => {
                if (draft) {
                    draft.startTime = startTime;
                    draft.endTime = endTime;
                }
            });
        });
    }, []);



    /**
     * Create the object that contain all the info needed for the MultiDataSourcesViewContainer to fetch data. This object will contain
     * all the collection info, smart module info, metric definition info, and metric info.
     */
    const fetchDataParams = useMemo((): readonly FetchCollectionDataParams[] | undefined => {
        if (alert && selectedEntity && entityClasses) {
            const numberOfColors = theme.palette.chart.series.length;

            // This metricsCounter is used for selecting color from the theme's palette
            let metricsCounter = 0;

            const entityClass = entityClasses.find((entityClass) => {
                return entityClass.entityClass === selectedEntity.entityClass;
            });


            // Because 1 metricsDefinition is mapped to 1 collection so for each filteredMetricsDef, we will create 1
            // fetchCollectionDataParams
            const fetchCollectionDataParams = entityClass?.metricsDefinitions?.filter((metricDef) => {
                // Filter out all metric definitions that are NOT TSDB
                return metricDef.dataStoreConfiguration.moduleType === SmartModuleType.TSDB;
            }).map((metricDef): FetchCollectionDataParams => {


                // Create the fetchCollectionDataParams
                const collectionId = getMetricsDefinitionMetricsId(selectedEntity, metricDef.metricsDefinitionId);
                return {
                    homeId        : selectedEntity.homeId,
                    smartModuleId : metricDef.dataStoreConfiguration.moduleInstanceId,
                    collectionId,
                    numericMetrics: metricDef.numericMetrics?.map((metric): FetchDataNumericMetricParams => {
                        const color = numberOfColors > 0
                            ? theme.palette.chart.series[metricsCounter % numberOfColors].color : theme.palette.primary.dark;
                        const contrastText = numberOfColors > 0
                            ? theme.palette.chart.series[metricsCounter % numberOfColors].contrastText : undefined;
                        metricsCounter += 1;

                        return {
                            id               : createUniqueMetricId(collectionId, metric.name, true),
                            name             : metric.name,
                            displayName      : metric.evaluator?.displayName ?? metric.displayName ?? metric.name,
                            unit             : metric.unit,
                            color,
                            contrastTextColor: contrastText,
                        };
                    }),
                    tagMetrics: metricDef.tagMetrics?.map((metric): FetchDataTagMetricParams => {
                        const color = numberOfColors > 0
                            ? theme.palette.chart.series[metricsCounter % numberOfColors].color : theme.palette.primary.dark;
                        const contrastText = numberOfColors > 0
                            ? theme.palette.chart.series[metricsCounter % numberOfColors].contrastText : undefined;
                        metricsCounter += 1;
                        return {
                            id               : createUniqueMetricId(collectionId, metric.name, false),
                            name             : metric.name,
                            displayName      : metric.displayName ?? metric.name,
                            enumDefinitions  : metric.enumDefinitions,
                            color,
                            contrastTextColor: contrastText,
                        };
                    }),
                    metricDisplayNamePrefix: metricDef.displayName ?? metricDef.metricsDefinitionId,
                    metricsDefinitionId    : metricDef.metricsDefinitionId,
                };
            });

            return fetchCollectionDataParams;
        }

        return undefined;
    }, [alert, entityClasses, selectedEntity, theme.palette.chart.series, theme.palette.primary.dark]);



    /**
     * If the Alert is an instance of MetricAlert, it means it was triggered by one of the entity's metrics. So when we show data initially, we
     * want to show only the data for that 1 metric that triggered the alert instead of showing all metrics of the entity. Therefore, we will
     * pass an object containing the "initialSelectedMetricIdsMap" to the MultiDataSourcesViewContainer. This map will have ALL the
     * entity's metrics DISABLED except for the metric that triggered the alert.
     */
    const initialSelectedMetricIdsMap = useMemo((): MultiDataSourcesViewContainerProps['initialSelectedMetricIdsMap'] => {
        if (alert && selectedEntity && isMetricsAlert(alert)) {
            const collectionId = getMetricsDefinitionMetricsId(selectedEntity, alert.metricsDefinitionId);
            const alertTargetNumericMetricId = createUniqueMetricId(collectionId, alert.metricName, true);

            return {
                numericMetrics: {
                    [alertTargetNumericMetricId]: true,
                },
                tagMetrics: {},
            };
        }

        return undefined;
    }, [alert, selectedEntity]);



    // return the time the alert occurred, we will display a vertical line to show when the alert occurred
    const alertInvocationTime = useMemo((): number | undefined => {
        if (alert) {
            return moment(alert.invocationTime).valueOf();
        }

        return undefined;
    }, [alert]);



    /**
     * On show alert info clicked, dispatch an action to show the Preview panel for the selected alert
     */
    const onShowAlertInfo = useCallback(() => {
        if (alert) {
            // Dispatch an action to show the Preview Panel for the selected alert.
            const options: AlertPreviewCompOptions = {
                location : SlideOutPanelLocation.RIGHT,
                compType : SlideOutPanelCompType.ALERT_PREVIEW,
                compProps: {
                    data: {
                        projectId: props.projectId,
                        alertId  : alert.id,
                    },
                },
            };
            uiDispatch(showSlideOutPanel(options));
        }
    }, [alert, props.projectId, uiDispatch]);



    /**
     * On show entity info clicked, dispatch an action to show the Preview panel for the entity linked to the alert
     */
    const onShowEntityInfo = useCallback(() => {
        if (alert) {
            // Dispatch an action to show the Preview Panel for the selected alert.
            const options: EntityPreviewCompOptions = {
                location : SlideOutPanelLocation.RIGHT,
                compType : SlideOutPanelCompType.ENTITY_PREVIEW,
                compProps: {
                    data: {
                        projectId: props.projectId,
                        entityId : alert.entityId,
                    },
                },
            };
            uiDispatch(showSlideOutPanel(options));
        }
    }, [alert, props.projectId, uiDispatch]);


    /**
     * Create a cache the control bar
     */
    const controlBar = useMemo(() => {
        // Wait until we have created 'fetchDataOptions' before creating this control bar because we need to initialize the control bar
        // with the initial start/end time.
        if (fetchDataOptions) {
            return (
                <div className={classes.controlBar}>
                    <TimeRangeControlBarContainer
                        className={clsx(classes.timeControl)}
                        projectId={props.projectId}
                        startTime={fetchDataOptions.startTime}
                        endTime={fetchDataOptions.endTime}
                        timeControlSettings={timeControlSettings}
                        onInputsChanged={onTimeRangeChanged}
                    />
                    <div className={classes.actionsContainer}>
                        <Button
                            className={classes.action}
                            variant="contained"
                            color="default"
                            startIcon={isTabletOrLarger ? <Icon>notifications_active</Icon> : undefined}
                            onClick={onShowAlertInfo}
                        >
                            {isTabletOrLarger ? (
                                // Show button label for medium screen or larger or if there is no icon
                                capitalizeAll(trans('alerting.label.alert'))
                            ) : (
                                <Icon>notifications_active</Icon>
                            )}
                        </Button>
                        <Button
                            className={classes.action}
                            variant="contained"
                            color="default"
                            startIcon={isTabletOrLarger ? <Icon>widgets</Icon> : undefined}
                            onClick={onShowEntityInfo}
                        >
                            {isTabletOrLarger ? (
                                // Show button label for medium screen or larger or if there is no icon
                                capitalizeAll(trans('terms.entity'))
                            ) : (
                                <Icon>widgets</Icon>
                            )}
                        </Button>
                    </div>
                </div>
            );
        }
        return undefined;
    }, [fetchDataOptions, classes.controlBar, classes.timeControl, classes.actionsContainer, classes.action, props.projectId, onTimeRangeChanged,
        isTabletOrLarger, onShowAlertInfo, trans, onShowEntityInfo]);



    /**
     * This useEffect is used for attaching the Control Bar to the top nav when this component is initialized and remove the control
     * bar from the top nav when this component is destroyed.
     */
    useEffect(() => {
        uiDispatch(addControlPanelComp(INVESTIGATE_ALERT_CONTROL_BAR, controlBar, ControlPanelCompPriority.HIGH));

        return () => {
            uiDispatch(removeControlPanelComp(INVESTIGATE_ALERT_CONTROL_BAR));
        };
    }, [controlBar, uiDispatch]);



    return (
        <>
            {props.projectId && alert && selectedEntity && fetchDataOptions && fetchDataParams ? (
                <MultiDataSourcesViewContainer
                    projectId={props.projectId}
                    chartIdPrefix={`investigate-issue-chart-${alert.id}`}
                    title={selectedEntity.displaySettings.name}
                    fetchDataOptions={fetchDataOptions}
                    fetchCollectionsDataParams={fetchDataParams}
                    initialSelectedMetricIdsMap={initialSelectedMetricIdsMap}
                    chartViewSettings={chartViewSettings}
                    metricViewSettings={metricViewSettings}
                    showVerticalLineAtTimestamp={alertInvocationTime}
                />
            ) : (
                <CircularProgress />
            )}
        </>
    );
};


// Register this component with the RitzComponentsCollection so that it can be used with the Ritz UI customization system
RitzComponentsCollection.getInstance().registerComponent('InvestigateAlertContainer', InvestigateAlertContainer);
